import React, { useState, useEffect, useRef } from 'react';
import './ChatPanel.css';

function ChatPanel({ roomName, user }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messageIdsRef = useRef(new Set()); // Track message IDs to prevent duplicates
  const lastFetchTimeRef = useRef(Date.now());

  // Debugging
  console.log('ChatPanel initializing for room:', roomName);
  
  // Fetch initial messages when component mounts and set up polling
  useEffect(() => {
    fetchMessages(true); // Initial fetch
    
    // Set up polling to fetch new messages every few seconds
    pollIntervalRef.current = setInterval(() => {
      fetchMessages(false); // Subsequent fetches
    }, 3000); // Poll every 3 seconds
    
    return () => {
      // Clean up interval when component unmounts
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);

  // Auto scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async (isInitialFetch) => {
    try {
      if (isInitialFetch) {
        setIsLoading(true);
      }
      
      setError('');
      
      // Use a timestamp query parameter to only fetch new messages
      let url = `/api/chat/${encodeURIComponent(roomName)}`;
      
      // If this is not the initial fetch, only get newer messages
      if (!isInitialFetch) {
        url += `?since=${lastFetchTimeRef.current}`;
      }
      
      console.log(`Fetching messages for room: ${roomName}${isInitialFetch ? ' (initial)' : ' (polling)'}`);
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }
      
      const data = await response.json();
      
      // Update the last fetch time to now
      lastFetchTimeRef.current = Date.now();
      
      if (data.length === 0) {
        // No new messages
        return;
      }
      
      console.log(`Fetched ${data.length} message(s) from server`);
      
      // If initial fetch, replace all messages
      if (isInitialFetch) {
        // Update tracking set with all message IDs
        messageIdsRef.current = new Set(data.map(msg => msg.id));
        setMessages(data);
      } else {
        // Otherwise, only add new messages we haven't seen before
        const newMessages = data.filter(msg => !messageIdsRef.current.has(msg.id));
        
        if (newMessages.length > 0) {
          // Add new message IDs to our tracking set
          newMessages.forEach(msg => messageIdsRef.current.add(msg.id));
          
          // Add new messages to our state
          setMessages(prevMessages => [...prevMessages, ...newMessages]);
          console.log(`Added ${newMessages.length} new messages`);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      if (isInitialFetch) {
        setError('Failed to load messages');
      }
    } finally {
      if (isInitialFetch) {
        setIsLoading(false);
      }
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setError('');
      
      // Create a temporary message for immediate display
      const tempMessage = {
        id: `temp-${Date.now()}`,
        user_id: user.id, 
        user_name: user.name,
        message: newMessage.trim(),
        room_name: roomName,
        created_at: new Date().toISOString()
      };
      
      // Add to messages immediately for responsive UI
      setMessages(prevMessages => [...prevMessages, tempMessage]);
      
      // Clear input
      setNewMessage('');
      
      // Send message via API
      console.log('Sending message via API');
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          roomName,
          message: tempMessage.message
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      // Get the real message ID from the response if available
      try {
        const responseData = await response.json();
        if (responseData.messageId) {
          // Replace the temp ID with the real one
          setMessages(prev => prev.map(msg => 
            msg.id === tempMessage.id 
              ? {...msg, id: responseData.messageId} 
              : msg
          ));
          
          // Update tracking set
          messageIdsRef.current.delete(tempMessage.id);
          messageIdsRef.current.add(responseData.messageId);
        }
      } catch (jsonError) {
        console.error('Error parsing API response:', jsonError);
      }
      
      // Immediately fetch newest messages to ensure we have the most current state
      fetchMessages(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <h3>Chat: {roomName}</h3>
      </div>
      
      <div className="chat-messages">
        {isLoading && <div className="loading-message">Loading messages...</div>}
        {error && <div className="error-message">{error}</div>}
        
        {messages.map((msg, index) => (
          <div 
            key={msg.id || `msg-${index}`}
            className={`message-container ${msg.user_id === user.id ? 'message-self-container' : ''}`}
          >
            <div className={`message ${msg.user_id === user.id ? 'message-self' : 'message-other'}`}>
              <div className="message-info">
                <span className="sender-name">
                  {msg.user_id === user.id ? 'You' : msg.user_name}
                </span>
                <span className="message-time">
                  {formatTimestamp(msg.created_at)}
                </span>
              </div>
              <div className="message-content">{msg.message}</div>
            </div>
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-input-container">
        <input
          type="text"
          value={newMessage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          className="chat-input"
        />
        <button
          onClick={sendMessage}
          disabled={!newMessage.trim()}
          className="send-button"
        >
          Send
        </button>
      </div>
    </div>
  );
}

export default ChatPanel;